// streamDeckBridge.ts — Pont entre le pupitre de la régie et le plugin Stream Deck.
//
// Le plugin Kleon (App/kleon-streamdeck) tourne dans l'application Stream Deck
// d'Elgato et ouvre un serveur WebSocket sur la boucle locale. La régie s'y
// connecte en CLIENT : une page web ne peut pas écouter, et c'est le plugin qui
// vit sur le poste de l'opérateur. Deux messages seulement circulent :
//
//   régie → plugin : `state`, l'instantané des touches du pupitre (libellés et
//                     états : à l'antenne, en preview, scène active…), renvoyé à
//                     chaque changement ;
//   plugin → régie : `command`, un appui (« source 3 à l'antenne », CUT, scène 2,
//                     média 5, AUTO) ou un tour de molette (T-bar, preview).
//
// Le plugin ne décide de rien : la régie exécute la commande exactement comme le
// clic sur le bouton correspondant, avec les mêmes garde-fous (régie qui pilote,
// source prête…). Les touches du Stream Deck ne sont qu'un second écran du pupitre.
//
// La connexion est À ACTIVER par l'opérateur (case mémorisée dans le navigateur) :
// tenter `ws://127.0.0.1` depuis une page publique déclenche, dans les Chrome
// récents, une demande d'autorisation « accès au réseau local » — rien à imposer
// aux régies qui n'ont pas de Stream Deck.

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

/** Port d'écoute du plugin. À changer des deux côtés à la fois (plugin : src/bridge.ts). */
export const STREAM_DECK_PORT = 53917;

/**
 * Version du protocole : le plugin l'affiche s'il ne la connaît pas.
 * 2 (2026-10-16) : sondages en direct — champ `polls` de l'instantané, commande
 * `poll`. Un plugin 1 ignore le champ ; un plugin 2 face à une régie 1 montre
 * ses touches Sondages « régie à mettre à jour ».
 * 3 (2026-10-16) : commandes de la barre de la régie — champ `controls`,
 * commande `control` (On air, Kleon.tv, Multi view, Sous-titres, Rec, Parler,
 * Pupitre, Média et scène).
 */
export const STREAM_DECK_PROTOCOL = 3;

const ENABLED_KEY = 'kleon_streamdeck_enabled';

// ─── Protocole ───────────────────────────────────────────────────────────────

/**
 * État d'une touche, déjà résolu par la régie : le plugin n'a qu'à le dessiner.
 * `active` veut dire « à l'antenne » pour une source, « en preview » pour la
 * rangée preview, « affichée / en cours » pour une scène ou un média.
 */
export type StreamDeckKeyState = 'empty' | 'avail' | 'active' | 'probing' | 'failed' | 'off';

export interface StreamDeckKey {
  label: string | null;
  state: StreamDeckKeyState;
}

export interface StreamDeckMediaKey extends StreamDeckKey {
  kind: 'image' | 'sound' | 'jingle' | null;
}

export interface StreamDeckSnapshot {
  /** Cette régie pilote (sinon : observation, seule la preview, locale, répond). */
  holder:     boolean;
  /** Régie allumée — éteinte, le pupitre est inerte à l'écran comme sur le Stream Deck. */
  powered:    boolean;
  channel:    string | null;
  program:    StreamDeckKey[];
  preview:    StreamDeckKey[];
  scenes:     StreamDeckKey[];
  media:      StreamDeckMediaKey[];
  cutEnabled: boolean;
  autoActive: boolean;
  /** Position de la T-bar, 0 à 1, et le côté où elle repose entre deux transitions. */
  tbar:       number;
  tbarBaseline: 0 | 1;
  /** Libellés de ce qui est à l'antenne et en preview, pour les écrans des molettes. */
  onAirLabel:     string | null;
  inPreviewLabel: string | null;
  /** Sondages en direct (protocole 2) : tenus par PollsDock, cf. plus bas. */
  polls?:         StreamDeckPolls;
  /** Commandes de la barre de la régie (protocole 3), cf. plus bas. */
  controls?:      StreamDeckControls;
}

/**
 * Commandes de la régie hors pupitre (protocole 3) : chacune est publiée par le
 * composant qui porte son bouton à l'écran (ControlBar, SubtitlesControl,
 * l'intercom de MultiviewPanelV2), et exécutée par lui, comme un clic.
 */
export type StreamDeckControlName =
  | 'onAir' | 'portal' | 'multiview' | 'subtitles' | 'rec' | 'talk' | 'pupitre' | 'media'
  // Nombre de spectateurs (ajouté au protocole 3 le 2026-10-16 : un plugin qui ne
  // le connaît pas l'ignore) — `detail` porte le nombre, un appui le relève.
  | 'viewers';

export interface StreamDeckControl {
  /** Le bouton de la régie répondrait à un clic maintenant. */
  available: boolean;
  /** Allumé : à l'antenne, visible sur Kleon.tv, enregistrement en cours, micro ouvert… */
  active:    boolean;
  /** Un changement est en cours (démarrage, arrêt, appel au serveur). */
  busy:      boolean;
  /** Une précision pour la touche : durée de l'enregistrement, langue, caméras visées… */
  detail:    string | null;
}

export type StreamDeckControls = Partial<Record<StreamDeckControlName, StreamDeckControl>>;

/**
 * Sondages en direct vus du Stream Deck : les sondages préparés (une touche
 * chacun, dans l'ordre de la colonne « Préparés » du panneau), et celui en
 * cours. Les résultats ne sont jamais montrés avant la clôture si le sondage
 * les cache aux spectateurs : le Stream Deck est souvent dans le champ.
 */
export interface StreamDeckPolls {
  /** Faux pour une chaîne des apps, ou un serveur qui ne connaît pas les sondages. */
  available: boolean;
  /** Un geste est en cours d'envoi au serveur : les touches attendent. */
  busy:      boolean;
  drafts:    { label: string; quiz: boolean }[];
  current:   null | {
    question:      string;
    status:        'open' | 'closed';
    /** Secondes avant la clôture d'office, null = sans limite (ou clos). */
    remaining:     number | null;
    onAir:         boolean;
    totalVotes:    number;
    quiz:          boolean;
    /** Réponse en tête et son pourcentage, null tant qu'ils sont cachés. */
    leader:        string | null;
    leaderPercent: number | null;
  };
}

/** Sondages préparés que le plugin peut montrer (une rangée de 9 touches). */
export const STREAM_DECK_POLL_DRAFTS = 9;

export type StreamDeckCommand =
  | { type: 'press'; target: 'program' | 'preview' | 'scene' | 'media'; index: number }
  | { type: 'press'; target: 'cut' | 'auto' }
  | { type: 'tbar'; delta: number }
  | { type: 'previewStep'; delta: number }
  // Sondages (protocole 2) : lancer le préparé n° `index` ; `end` clôt le sondage
  // ouvert, ou retire le résultat d'un sondage clos ; `onAir` bascule le bandeau.
  | { type: 'poll'; action: 'launch'; index: number }
  | { type: 'poll'; action: 'end' | 'onAir' }
  // Commandes de la régie (protocole 3). `start` / `stop` plutôt qu'une bascule
  // pour On air et Rec : le plugin n'arrête qu'après un appui long, et un ordre
  // explicite ne peut pas rallumer ce qu'on voulait éteindre si l'état a changé
  // entre-temps. `toggle` pour le reste.
  | { type: 'control'; control: StreamDeckControlName; action: 'start' | 'stop' | 'toggle' };

export type StreamDeckPollCommand = Extract<StreamDeckCommand, { type: 'poll' }>;
export type StreamDeckControlCommand = Extract<StreamDeckCommand, { type: 'control' }>;

// ─── Commandes de la régie : relais vers le pupitre ──────────────────────────
//
// Même motif que les sondages : chaque bouton publie son état et son
// gestionnaire ; le pupitre joint les états à l'instantané et passe les appuis.

let controlsState: StreamDeckControls = {};
const controlHandlers = new Map<StreamDeckControlName, (cmd: StreamDeckControlCommand) => void>();
const controlsListeners = new Set<() => void>();

export const publishStreamDeckControl = (name: StreamDeckControlName, state: StreamDeckControl | undefined) => {
  if (JSON.stringify(state) === JSON.stringify(controlsState[name])) return;
  const next = { ...controlsState };
  if (state) next[name] = state; else delete next[name];
  controlsState = next;
  controlsListeners.forEach(l => l());
};

export const setStreamDeckControlHandler = (
  name: StreamDeckControlName,
  handler: ((cmd: StreamDeckControlCommand) => void) | null,
) => {
  if (handler) controlHandlers.set(name, handler); else controlHandlers.delete(name);
};

export const runStreamDeckControlCommand = (cmd: StreamDeckControlCommand) => {
  controlHandlers.get(cmd.control)?.(cmd);
};

const subscribeControls = (listener: () => void) => {
  controlsListeners.add(listener);
  return () => { controlsListeners.delete(listener); };
};

/** États des commandes pour l'instantané. */
export const useStreamDeckControls = (): StreamDeckControls =>
  useSyncExternalStore(subscribeControls, () => controlsState);

/**
 * Publie un bouton et son gestionnaire tant que le composant est monté.
 * `handler` peut changer à chaque rendu : c'est toujours le dernier qui répond.
 */
export function useStreamDeckControl(
  name: StreamDeckControlName,
  state: StreamDeckControl | undefined,
  handler: (cmd: StreamDeckControlCommand) => void,
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    setStreamDeckControlHandler(name, cmd => handlerRef.current(cmd));
    return () => { setStreamDeckControlHandler(name, null); publishStreamDeckControl(name, undefined); };
  }, [name]);
  const json = JSON.stringify(state ?? null);
  useEffect(() => {
    publishStreamDeckControl(name, state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, json]);
}

// ─── Sondages : relais entre PollsDock et le pupitre ─────────────────────────
//
// Le pont vit dans le pupitre (DeckV2), les sondages dans leur panneau
// (PollsDock), deux composants sans lien direct. Le panneau publie ici son état
// et son gestionnaire de commandes ; le pupitre les lit pour l'instantané et
// lui passe les appuis des touches Sondages.

let pollsState: StreamDeckPolls | undefined;
let pollsHandler: ((cmd: StreamDeckPollCommand) => void) | null = null;
const pollsListeners = new Set<() => void>();

export const publishStreamDeckPolls = (next: StreamDeckPolls | undefined) => {
  if (JSON.stringify(next) === JSON.stringify(pollsState)) return;
  pollsState = next;
  pollsListeners.forEach(l => l());
};

export const setStreamDeckPollsHandler = (handler: ((cmd: StreamDeckPollCommand) => void) | null) => {
  pollsHandler = handler;
};

export const runStreamDeckPollCommand = (cmd: StreamDeckPollCommand) => {
  pollsHandler?.(cmd);
};

const subscribePolls = (listener: () => void) => {
  pollsListeners.add(listener);
  return () => { pollsListeners.delete(listener); };
};

/** État des sondages pour l'instantané ; undefined tant que le panneau n'a rien publié. */
export const useStreamDeckPolls = (): StreamDeckPolls | undefined =>
  useSyncExternalStore(subscribePolls, () => pollsState);

type IncomingMessage = { type: 'command'; command: StreamDeckCommand };

// ─── Réglage mémorisé ────────────────────────────────────────────────────────

export const loadStreamDeckEnabled = (): boolean => {
  try { return localStorage.getItem(ENABLED_KEY) === '1'; } catch { return false; }
};

export const saveStreamDeckEnabled = (on: boolean) => {
  try { localStorage.setItem(ENABLED_KEY, on ? '1' : '0'); } catch {}
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export type StreamDeckStatus = 'off' | 'searching' | 'connected';

/**
 * Tient la connexion au plugin tant que `enabled` est vrai, lui envoie
 * l'instantané à chaque changement et remet les commandes reçues à `onCommand`.
 *
 * Le plugin absent (application Stream Deck fermée), on réessaie de plus en plus
 * rarement — 2 s, puis jusqu'à 15 s — : chaque tentative ratée écrit une ligne
 * d'erreur dans la console du navigateur, qu'aucun code ne peut taire.
 */
export function useStreamDeckBridge(
  enabled: boolean,
  snapshot: StreamDeckSnapshot,
  onCommand: (cmd: StreamDeckCommand) => void,
): StreamDeckStatus {
  const [status, setStatus] = useState<StreamDeckStatus>(enabled ? 'searching' : 'off');

  const wsRef        = useRef<WebSocket | null>(null);
  const lastSentRef  = useRef<string | null>(null);
  const snapshotRef  = useRef(snapshot);
  const onCommandRef = useRef(onCommand);
  snapshotRef.current  = snapshot;
  onCommandRef.current = onCommand;

  useEffect(() => {
    if (!enabled) { setStatus('off'); return; }

    let disposed = false;
    let retryDelay = 2000;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const sendSnapshot = (ws: WebSocket) => {
      const json = JSON.stringify({ type: 'state', state: snapshotRef.current });
      if (json === lastSentRef.current) return;
      lastSentRef.current = json;
      ws.send(json);
    };

    const connect = () => {
      if (disposed) return;
      setStatus('searching');
      let ws: WebSocket;
      try {
        ws = new WebSocket(`ws://127.0.0.1:${STREAM_DECK_PORT}`);
      } catch {
        schedule();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        retryDelay = 2000;
        setStatus('connected');
        ws.send(JSON.stringify({ type: 'hello', app: 'kleon-regie', protocol: STREAM_DECK_PROTOCOL }));
        lastSentRef.current = null;
        sendSnapshot(ws);
      };
      ws.onmessage = (ev) => {
        let msg: IncomingMessage | null = null;
        try { msg = JSON.parse(String(ev.data)); } catch { return; }
        if (msg?.type === 'command' && msg.command) onCommandRef.current(msg.command);
      };
      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        if (disposed) return;
        setStatus('searching');
        schedule();
      };
      // onerror est toujours suivi de onclose : c'est lui qui reprogramme.
      ws.onerror = () => {};
    };

    const schedule = () => {
      if (disposed) return;
      retryTimer = setTimeout(connect, retryDelay);
      retryDelay = Math.min(retryDelay * 2, 15000);
    };

    connect();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      const ws = wsRef.current;
      wsRef.current = null;
      lastSentRef.current = null;
      if (ws) { ws.onclose = null; ws.close(); }
    };
  }, [enabled]);

  // L'instantané est recalculé à chaque rendu du pupitre ; on ne l'envoie que
  // s'il a changé (comparaison du JSON, quelques centaines d'octets).
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const json = JSON.stringify({ type: 'state', state: snapshot });
    if (json === lastSentRef.current) return;
    lastSentRef.current = json;
    ws.send(json);
  });

  return status;
}
